import { useDispatch } from 'react-redux';
import { useState } from 'react';
import {
  fetchAreasLookup,
  fetchUnitAreas,
  addUnitArea,
  addUnitAreaOther,
  fetchSuiteCounts as fetchSuiteCountsApi,
  getAssetsBySuiteId,
} from 'src/services/client';
import { saveAssetsDone } from 'src/store/actions/assets';
import { fetchCountsDone } from 'src/store/actions/properties';
import { toast } from 'react-toastify';

export interface LookupArea {
  id: string;
  name: string;
}

export interface UnitArea {
  id: string;
  areaId: string;
  areaName: string;
  customLabel: string | null;
  type: string | null;
}

interface UseSuiteAreasOptions {
  suiteId: string;
  userId: string;
  roleName: string;
}

const useSuiteAreas = ({ suiteId, userId, roleName }: UseSuiteAreasOptions) => {
  const dispatch = useDispatch();

  const [lookupAreas, setLookupAreas] = useState<LookupArea[]>([]);
  const [unitAreas, setUnitAreas] = useState<UnitArea[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);


  /* Private helpers (Redux refresh) same waterfall pattern as useUnitAreas.deleteUnitArea:
   * 1. Re-fetch assets → dispatch into global Redux
   * 2. Re-fetch suite counts → dispatch into global Redux
   * TODO: Remove + Uncouple this from Redux. Pretty horrible tech debt. */
  const refreshGlobalAssets = async () => {
    try {
      const response: any = await getAssetsBySuiteId(suiteId, userId, roleName);

      if (response.ok || response.status === 200) {
        const json: any = await response.json();
        dispatch(saveAssetsDone(json.data));
      }
    } catch (err: any) {
      console.error('[useSuiteAreas] Failed to refresh assets:', err?.message);
    }
  };

  const refreshSuiteCounts = async () => {
    try {
      const response: any = await fetchSuiteCountsApi(suiteId, roleName);
      if (response.ok || response.status === 200) {
        const json = await response.json();
        if (json.statusCode === 200) {
          dispatch(fetchCountsDone(json.data));
        }
      }
    } catch (err: any) {
      console.error('[useSuiteAreas] Failed to refresh suite counts:', err?.message);
    }
  };

  /**
   * Fetch both data sources in parallel when the dialog opens:
   * 1. Preconfigured areas (tenant-level lookup)
   * 2. Areas already on this unit (for "already added" cross-reference)
   */
  const fetchAreas = async () => {
    setLoading(true);
    setError(null);

    try {
      const [lookupResponse, unitResponse]: any[] = await Promise.all([
        fetchAreasLookup(),
        fetchUnitAreas(suiteId),
      ]);

      if (lookupResponse.ok || lookupResponse.status === 200) {
        const lookupJson = await lookupResponse.json();

        // TODO: Confirm the response shape from GET /api/units/areas/lookup.
        // If the API wraps in a `data` property, use lookupJson.data.
        // If the API returns a flat array, use lookupJson directly.
        const availableRooms: LookupArea[] = Array.isArray(lookupJson)
          ? lookupJson
          : lookupJson.data || [];

        setLookupAreas(availableRooms);
      } else {
        setError('Failed to load available rooms.');
        setLoading(false);
        return;
      }

      if (unitResponse.ok || unitResponse.status === 200) {
        const unitJson = await unitResponse.json();

        const existingRooms: UnitArea[] = Array.isArray(unitJson)
          ? unitJson
          : unitJson.data || [];

        setUnitAreas(existingRooms);
      } else {
        console.warn('[useSuiteAreas] Could not fetch unit areas for cross-reference.');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load room data.');
    } finally {
      setLoading(false);
    }
  };

  const addPreconfiguredRoom = async (areaId: string, customLabel?: string): Promise<boolean> => {
    setSubmitting(true);
    setError(null);

    try {
      const body: any = { unitId: suiteId, areaId };
      if (customLabel) body.CustomLabel = customLabel;

      const response: any = await addUnitArea(body);

      if (response.ok || response.status === 200) {
        await refreshGlobalAssets();
        await refreshSuiteCounts();

        toast.success(` ⓘ ${customLabel || 'Room'} has been added.`, {
          autoClose: 4000,
          position: 'top-center',
        });
        return true;
      } else {
        const errorJson = await response.json().catch(() => null);
        setError(errorJson?.message || `Failed to add room. (Status ${response.status})`);
        return false;
      }
    } catch (err: any) {
      setError(err?.message || 'Something went wrong while adding the room.');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const addCustomRoom = async (customLabel: string): Promise<boolean> => {
    setSubmitting(true);
    setError(null);

    try {
      const response: any = await addUnitAreaOther({
        customLabel,
        unitId: suiteId,
      });

      if (response.ok || response.status === 200) {
        await refreshGlobalAssets();
        await refreshSuiteCounts();

        toast.success(` ⓘ ${customLabel} has been added.`, {
          autoClose: 4000,
          position: 'top-center',
        });
        return true;
      } else {
        const errorJson = await response.json().catch(() => null);
        setError(errorJson?.message || `Failed to create room. (Status ${response.status})`);
        return false;
      }
    } catch (err: any) {
      setError(err?.message || 'Something went wrong while creating the room.');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const clearError = () => setError(null);

  return {
    lookupAreas,
    unitAreas,
    loading,
    submitting,
    error,
    clearError,
    fetchAreas,
    addPreconfiguredRoom,
    addCustomRoom,
  };
};

export default useSuiteAreas;